import { AppDispatch } from '../..';
import {
  DbId,
  TAffiliationSign,
  TTreeId,
} from '../../../models/role-privs/RolePrivsData';
import ContextService from '../../../services/context/ContextService';
import IContextService from '../../../services/context/IContextService';
import IIntlService from '../../../services/intl/IIntlService';
import IntlService from '../../../services/intl/IntlService';
import IRolePrivsService from '../../../services/role-privs/IRolePrivsService';
import RolePrivsService from '../../../services/role-privs/RolePrivsService';
import IUUIDService from '../../../services/uid/IUUIDService';
import UUIDService from '../../../services/uid/UUIDService';
import { AppState } from '../../reducers/root';
import { getCategoryState } from '../../selectors/category';
import { getDatasetCategoryId } from '../../selectors/dataset';
import {
  findCategoryTreeById,
  findMainTreeById,
  findMainTreeByPane,
  getIsCategoryTree,
  getPrivsCategoryName,
  getPrivsCategoryTreeActiveCategoryName,
  getPrivsInherit,
  getPrivsRoleId,
  getPrivsTreeActiveId,
  getPrivsTreeState,
  getTreeNodesChecked,
  getTreePane,
} from '../../selectors/rolePrivs';
import {
  CategoryTreeNode,
  IExtendedTreeNodeInstance,
} from '../../slices/dm-trees/CategoryTreeNode';
import rolePrivs, { ICategoryPaneFields } from '../../slices/rolePrivs';
import {
  apiCall,
  createAppListener,
  postDispatch,
  postDispatchAny,
} from '../listenerHelpers';

const rolePrivsListener = createAppListener();

const idService: IUUIDService = new UUIDService();
const intlService: IIntlService = new IntlService();
const contextService: IContextService = new ContextService();
const rolePrivsService: IRolePrivsService = new RolePrivsService();

enum EFieldsNamesMap {
  categoryName = 'SUNITCODE',
  catalog = 'NSIGN_HIER',
  subdivision = 'NSIGN_JURPERS',
  hierarchy = 'NHIERARCHICAL',
}

let timer: ReturnType<typeof setTimeout>;

const findNode = (
  state: AppState,
  treeId: TTreeId,
  nodeId: DbId
): CategoryTreeNode => {
  const tree = getPrivsTreeState(state, treeId);
  const node = tree.nodes.find(n => n.id === nodeId);

  if (!node) {
    throw new Error(`Node ${nodeId} does not exist`);
  }

  return node;
};

const nodeHasChildren = (
  state: AppState,
  treeId: TTreeId,
  nodeId: DbId
): boolean => {
  return findNode(state, treeId, nodeId).hasChildren;
};

const childrenAreLoaded = (
  state: AppState,
  treeId: TTreeId,
  nodeId: DbId
): boolean => {
  return getPrivsTreeState(state, treeId).childrenCache.has(nodeId);
};

const shouldLoadChildren = (
  state: AppState,
  treeId: TTreeId,
  nodeId: DbId
): boolean => {
  return (
    nodeHasChildren(state, treeId, nodeId) &&
    !childrenAreLoaded(state, treeId, nodeId)
  );
};

const debounceAction = (action: () => void, timeout = 300): void => {
  if (timer) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => action(), timeout);
};

const updateActions = (
  state: AppState,
  dispatch: AppDispatch,
  treeId: TTreeId,
  nextActiveId: number
): void => {
  const pane = getTreePane(state, treeId);
  const isCategoryTree = getIsCategoryTree(state, treeId);

  if (pane === 'all') {
    const categoryName =
      getPrivsCategoryTreeActiveCategoryName(state, treeId) ?? '';
    dispatch(
      rolePrivs.loadCategoryTreeActions({
        categoryTreeId: treeId,
        categoryName,
      })
    );
  } else {
    if (!isCategoryTree && pane === 'catalog') {
      dispatch(rolePrivs.loadMainTreeActions({ treeId, nodeId: nextActiveId }));
    }
    const { categoryTreeId } = findMainTreeByPane(state, pane);
    if (categoryTreeId) {
      const categoryName =
        getPrivsCategoryTreeActiveCategoryName(state, categoryTreeId) ?? '';
      dispatch(
        rolePrivs.loadCategoryTreeActions({
          categoryTreeId,
          categoryName,
        })
      );
    }
  }
};

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.open,
      success: rolePrivs.openSuccess,
      failure: rolePrivs.openFailure,
    },
    async (datasetId, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);

      const context = await contextService.getContext(
        categoryState,
        state.dataset,
        datasetId
      );

      const { SUNITCODE: categoryName, ...contextRow } = context.get('Row');

      const roleId = context.get('Parent');

      const panes: ICategoryPaneFields[] = [];
      (['catalog', 'hierarchy', 'subdivision'] as const).forEach(
        sign =>
          contextRow[EFieldsNamesMap[sign]] &&
          panes.push({
            categoryTreeId: idService.generateId(),
            pane: sign,
          })
      );

      if (!panes.length) {
        panes.push({
          categoryTreeId: idService.generateId(),
          pane: 'all',
        });
      }

      return {
        categoryName,
        roleId,
        context,
        panes,
        dataset: datasetId,
      };
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.openSuccess,
    ({ categoryName, roleId, panes }, _state, dispatch) => {
      dispatch(rolePrivs.loadCategoryTreeRoot({ categoryName, roleId, panes }));
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.loadCategoryTreeRoot,
      success: rolePrivs.loadCategoryTreeRootSuccess,
      failure: rolePrivs.loadCategoryTreeRootFailure,
    },
    async ({ categoryName, roleId, panes }) => {
      const categoryNodes: IExtendedTreeNodeInstance[] =
        await rolePrivsService.loadCategoryTree(categoryName);

      for (const node of categoryNodes) {
        node.name = await intlService.getCategoryTitle(node.label);
      }

      return { categoryName, roleId, panes, categoryNodes };
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.loadCategoryTreeRootSuccess,
    ({ panes, categoryNodes }, _state, dispatch) => {
      for (const { categoryTreeId, pane } of panes) {
        dispatch(
          rolePrivs.selectTreeNode({
            treeId: categoryTreeId,
            nodeId: categoryNodes[0].id,
          })
        );
        if (categoryNodes[0].hasChildren) {
          dispatch(
            rolePrivs.expandTreeNode({
              treeId: categoryTreeId,
              nodeId: categoryNodes[0].id,
            })
          );
        }
        if (pane !== 'all') {
          dispatch(rolePrivs.loadMainTreeRoot({ categoryTreeId, pane }));
        }
      }
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.loadMainTreeRoot,
      success: rolePrivs.loadMainTreeRootSuccess,
      failure: rolePrivs.loadMainTreeRootFailure,
    },
    async ({ categoryTreeId, pane }, state) => {
      const mainTreeId = idService.generateId();
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const mainTreeNodes: IExtendedTreeNodeInstance[] =
        await rolePrivsService.loadTree(categoryName, roleId, pane);
      return {
        treeId: mainTreeId,
        categoryTreeId,
        pane,
        mainTreeNodes,
      };
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.loadMainTreeRootSuccess,
    ({ treeId, mainTreeNodes }, _state, dispatch) => {
      if (mainTreeNodes.length) {
        dispatch(
          rolePrivs.selectTreeNode({ treeId, nodeId: mainTreeNodes[0].id })
        );
        if (mainTreeNodes[0].hasChildren) {
          dispatch(
            rolePrivs.expandTreeNode({
              treeId,
              nodeId: mainTreeNodes[0].id,
            })
          );
        }
      }
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.expandTreeNode,
    ({ treeId, nodeId }, state, dispatch) => {
      const selectedNodeId = nodeId ?? getPrivsTreeActiveId(state, treeId);
      if (
        selectedNodeId !== null &&
        shouldLoadChildren(state, treeId, selectedNodeId)
      ) {
        dispatch(
          rolePrivs.loadTreeNodeChildren({ treeId, nodeId: selectedNodeId })
        );
      }
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.loadTreeNodeChildren,
      success: rolePrivs.loadTreeNodeChildrenSuccess,
      failure: rolePrivs.loadTreeNodeChildrenFailure,
    },
    async ({ treeId, nodeId }, state, dispatch) => {
      dispatch(rolePrivs.markTreeNodePending({ treeId, nodeId }));
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const pane = getTreePane(state, treeId);
      const isCategoryTree = getIsCategoryTree(state, treeId);
      const nodes: IExtendedTreeNodeInstance[] =
        isCategoryTree || pane === 'all'
          ? await rolePrivsService.loadCategoryTree(categoryName, nodeId)
          : await rolePrivsService.loadTree(categoryName, roleId, pane, nodeId);

      if (isCategoryTree || pane === 'all') {
        for (const node of nodes) {
          node.name = await intlService.getCategoryTitle(node.label);
        }
      }

      return { treeId, nodeId, nodes };
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.loadTreeNodeChildrenSuccess,
    ({ treeId, nodeId }, _state, dispatch) => {
      dispatch(rolePrivs.unmarkTreeNodePending({ treeId, nodeId }));
    }
  )
);

rolePrivsListener.startListening(
  postDispatchAny(
    [rolePrivs.selectTreeNode, rolePrivs.nextTreeNode, rolePrivs.prevTreeNode],
    ({ treeId }: { treeId: TTreeId }, state, dispatch, prevState, type) => {
      const prevActiveId = getPrivsTreeActiveId(prevState, treeId);
      const nextActiveId = getPrivsTreeActiveId(state, treeId) ?? 0;

      if (prevActiveId !== nextActiveId) {
        if (type !== rolePrivs.selectTreeNode.type) {
          debounceAction(() =>
            updateActions(state, dispatch, treeId, nextActiveId)
          );
        } else {
          updateActions(state, dispatch, treeId, nextActiveId);
        }
      }
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.loadMainTreeActions,
      success: rolePrivs.loadMainTreeActionsSuccess,
      failure: rolePrivs.loadMainTreeActionsFailure,
    },
    async ({ treeId, nodeId }, state) => {
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const catalogActionsData = await rolePrivsService.loadCatalogActions(
        categoryName,
        roleId,
        nodeId
      );

      return {
        treeId,
        nodeId,
        catalogActionsData,
      };
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.loadCategoryTreeActions,
      success: rolePrivs.loadCategoryTreeActionsSuccess,
      failure: rolePrivs.loadCategoryTreeActionsFailure,
    },
    async ({ categoryTreeId, categoryName }, state) => {
      const { mainTreeId, pane } = findCategoryTreeById(state, categoryTreeId);

      const params: Partial<Record<TAffiliationSign, DbId>> = {};
      if (mainTreeId) {
        const mainTreeActiveNodeId = getPrivsTreeActiveId(state, mainTreeId);
        if (pane !== 'all' && mainTreeActiveNodeId !== null) {
          params[pane] = mainTreeActiveNodeId;
        }
      }
      const roleId = getPrivsRoleId(state);
      const categoryActionsData = await rolePrivsService.loadCategoryActions(
        categoryName,
        roleId,
        params
      );

      for (const action of categoryActionsData) {
        action.label = await intlService.getCategoryFieldTitle(
          categoryName,
          action.label ||
            (action.type === 'Custom' ? action.actionCode : action.type)
        );
      }
      return { categoryTreeId, categoryActionsData };
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.toggleMainTreeNodeCheck,
      success: rolePrivs.toggleMainTreeNodeCheckSuccess,
      failure: rolePrivs.toggleMainTreeNodeCheckFailure,
    },
    async ({ treeId, pane, nodeId }, state) => {
      const selectedNodeId = nodeId ?? getPrivsTreeActiveId(state, treeId) ?? 0;
      if (pane === 'subdivision' && selectedNodeId === 0) {
        return;
      }
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const inherit = getPrivsInherit(state);
      const nodeIsChecked = getTreeNodesChecked(state, treeId, selectedNodeId);
      await rolePrivsService.setTreeNodePrivs(
        categoryName,
        roleId,
        pane,
        selectedNodeId,
        !nodeIsChecked,
        inherit
      );
      return {
        treeId,
        nodeId: selectedNodeId,
        checked: !nodeIsChecked,
      };
    }
  )
);

rolePrivsListener.startListening(
  postDispatch(
    rolePrivs.toggleMainTreeNodeCheckSuccess,
    (payload, state, dispatch) => {
      if (!payload) {
        return;
      }
      const { treeId, nodeId } = payload;
      const activeMainTreeNodeId = getPrivsTreeActiveId(state, treeId);

      if (activeMainTreeNodeId === nodeId) {
        updateActions(state, dispatch, treeId, activeMainTreeNodeId);
      }
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.setPrivs,
      success: rolePrivs.setPrivsSuccess,
      failure: rolePrivs.setPrivsFailure,
    },
    async ({ treeId, action }, state) => {
      const mainTreeActiveId = getPrivsTreeActiveId(state, treeId);
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const inherit = getPrivsInherit(state);

      if (mainTreeActiveId) {
        await rolePrivsService.setCatalogActionPrivs(
          categoryName,
          roleId,
          mainTreeActiveId,
          action.actionCode,
          !action.hasAccess,
          inherit
        );
      }

      return {
        treeId,
        actionCode: action.actionCode,
        hasAccess: !action.hasAccess,
      };
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.setCategoryPrivs,
      success: rolePrivs.setCategoryPrivsSuccess,
      failure: rolePrivs.setCategoryPrivsFailure,
    },
    async ({ treeId, action }, state) => {
      const { pane, mainTreeId } = findCategoryTreeById(state, treeId);
      const mainTreeActiveId = mainTreeId
        ? getPrivsTreeActiveId(state, mainTreeId)
        : undefined;

      const activeNodesIds: Partial<Record<TAffiliationSign, DbId>> = {};
      if (pane !== 'all' && mainTreeActiveId) {
        activeNodesIds[pane] = mainTreeActiveId;
      }

      const activeCategoryName =
        getPrivsCategoryTreeActiveCategoryName(state, treeId) ?? '';
      const roleId = getPrivsRoleId(state);
      const inherit = getPrivsInherit(state);

      if (activeCategoryName) {
        await rolePrivsService.setCategoryActionPrivs(
          activeCategoryName,
          roleId,
          activeNodesIds,
          action.actionCode,
          !action.hasAccess,
          inherit
        );
      }

      return {
        treeId,
        actionCode: action.actionCode,
        hasAccess: !action.hasAccess,
      };
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.setCatalogAllPrivs,
      success: rolePrivs.setCatalogAllPrivsSuccess,
      failure: rolePrivs.setCatalogAllPrivsFailure,
    },
    async (payload, state) => {
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const inherit = getPrivsInherit(state);
      await rolePrivsService.setCatalogAllActionsPrivs(
        categoryName,
        roleId,
        payload.catalogId,
        inherit
      );
      return payload;
    }
  )
);

rolePrivsListener.startListening(
  apiCall(
    {
      request: rolePrivs.setCategoryAllPrivs,
      success: rolePrivs.setCategoryAllPrivsSuccess,
      failure: rolePrivs.setCategoryAllPrivsFailure,
    },
    async ({ treeId, mainTreeActiveId }, state) => {
      const { categoryTreeId, pane } = findMainTreeById(state, treeId);
      const categoryName = getPrivsCategoryName(state);
      const roleId = getPrivsRoleId(state);
      const inherit = getPrivsInherit(state);
      await rolePrivsService.setCategoryAllActionsPrivs(
        categoryName,
        roleId,
        { [pane]: mainTreeActiveId },
        inherit
      );
      return { treeId: categoryTreeId };
    }
  )
);

export default rolePrivsListener.middleware;
